import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { EASE } from "@/lib/motion";
import { groupMonths, monthLabel, firstDow, formatNL, DAGKORT } from "@/lib/calendar";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem("bw_admin_token"));
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [busy, setBusy] = useState(false);
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState(null);
  const [label, setLabel] = useState("");

  const authHeaders = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${token}` });

  const loadAgenda = () =>
    fetch(`${BACKEND}/api/agenda?months=3`)
      .then((r) => r.json())
      .then(setData)
      .catch(() => {});

  const loadBookings = () =>
    fetch(`${BACKEND}/api/agenda/admin/bookings`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then(setBookings)
      .catch(() => {});

  useEffect(() => {
    if (!token) return undefined;
    loadAgenda();
    loadBookings();
    return undefined;
  }, [token]);

  const months = useMemo(() => (data ? groupMonths(data.days) : []), [data]);
  const monthKey = months[offset]?.[0];
  const days = months[offset]?.[1] ?? [];
  const blanks = monthKey ? firstDow(monthKey) : 0;
  const dayInfo = data?.days?.find((d) => d.date === selected);
  const dayBookings = bookings.filter((b) => b.date === selected);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const send = () =>
        fetch(`${BACKEND}/api/agenda/admin/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ password }),
        });
      let r = await send();
      if ([502, 503, 504].includes(r.status)) {
        // Server even onbereikbaar (bijv. net na een nieuwe publicatie): één keer opnieuw proberen
        await new Promise((res) => setTimeout(res, 1500));
        r = await send();
      }
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        if (typeof d.detail === "string") {
          setError(d.detail);
        } else if (r.status >= 500) {
          setError(`Server tijdelijk niet bereikbaar (fout ${r.status}). Probeer het over een minuut opnieuw.`);
        } else {
          setError(`Inloggen mislukt (fout ${r.status})`);
        }
        return;
      }
      const d = await r.json();
      sessionStorage.setItem("bw_admin_token", d.token);
      setToken(d.token);
    } catch {
      setError("Geen verbinding met de server");
    }
  };

  const logout = () => {
    sessionStorage.removeItem("bw_admin_token");
    setToken(null);
    setPassword("");
    setData(null);
    setBookings([]);
    setSelected(null);
  };

  const saveSettings = async (patch) => {
    if (!selected || !dayInfo || busy) return;
    setBusy(true);
    try {
      const body = {
        date: selected,
        closed: patch.closed ?? dayInfo.closed,
        maxBookings: patch.maxBookings ?? dayInfo.max,
      };
      const r = await fetch(`${BACKEND}/api/agenda/day`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      if (r.status === 401) {
        logout();
        return;
      }
      await loadAgenda();
    } catch {
      setError("Opslaan mislukt");
    }
    setBusy(false);
  };

  const addBooking = async () => {
    if (!selected || busy) return;
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND}/api/agenda/booking`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ date: selected, label }),
      });
      if (r.status === 401) {
        logout();
        return;
      }
      setLabel("");
      await loadBookings();
      await loadAgenda();
    } catch {
      setError("Toevoegen mislukt");
    }
    setBusy(false);
  };

  const delBooking = async (id) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND}/api/agenda/booking/${id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (r.status === 401) {
        logout();
        return;
      }
      await loadBookings();
      await loadAgenda();
    } catch {
      setError("Verwijderen mislukt");
    }
    setBusy(false);
  };

  return (
    <div className="admin-wrap" data-testid="admin-page">
      <motion.div className="admin-card" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}>
        <a href="/" className="brand" data-testid="admin-brand">
          <img src="/assets/logo.png" alt="" width="40" height="40" />
          <span>
            Blankwater<small>AGENDA BEHEER</small>
          </span>
        </a>
        {!token ? (
          <form onSubmit={login} className="admin-login" data-testid="admin-login-form">
            <label htmlFor="admin-password">Beheerderswachtwoord</label>
            <input
              id="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              autoFocus
              data-testid="admin-password-input"
            />
            {error ? (
              <p className="admin-error" role="alert" data-testid="admin-error">{error}</p>
            ) : null}
            <button type="submit" className="contact-ticket" data-testid="admin-login-button">
              Inloggen<span aria-hidden="true">↗</span>
            </button>
            <p className="form-help">Deze pagina is alleen voor Boris en Timo.</p>
          </form>
        ) : (
          <div data-testid="admin-panel">
            <div className="admin-bar">
              <h1>Agenda beheren</h1>
              <button type="button" className="nav-arrow" onClick={logout} data-testid="admin-logout">
                Uitloggen
              </button>
            </div>
            <p className="form-help">
              Klik op een dag om boekingen te beheren. Meerdere boekingen per dag zijn mogelijk;
              de dag is vol zodra alle plekken geboekt zijn.
            </p>
            {!monthKey ? (
              <p className="agenda-note" data-testid="admin-loading">Agenda laden…</p>
            ) : (
            <>
            <div className="agenda-head">
              <div className="agenda-month" data-testid="admin-month-label">{monthLabel(monthKey)}</div>
              <div className="agenda-nav">
                <button type="button" aria-label="Vorige maand" disabled={offset === 0} onClick={() => setOffset((o) => Math.max(0, o - 1))} data-testid="admin-prev">‹</button>
                <button type="button" aria-label="Volgende maand" disabled={offset === months.length - 1} onClick={() => setOffset((o) => Math.min(months.length - 1, o + 1))} data-testid="admin-next">›</button>
              </div>
            </div>
            <div className="agenda-grid admin-grid" data-testid="admin-grid">
              {DAGKORT.map((d) => (
                <div className="agenda-dow" key={d}>{d}</div>
              ))}
              {Array.from({ length: blanks }).map((_, i) => (
                <div className="agenda-day is-empty" key={`b-${i}`} aria-hidden="true" />
              ))}
              {days.map((day) => (
                <button
                  type="button"
                  key={day.date}
                  disabled={day.past}
                  onClick={() => setSelected(selected === day.date ? null : day.date)}
                  className={[
                    "agenda-day",
                    day.past ? "is-past" : `is-${day.status}`,
                    selected === day.date ? "is-picked" : "",
                  ].join(" ").trim()}
                  data-testid={`admin-day-${day.date}`}
                >
                  {Number(day.date.slice(8))}
                  {!day.past && day.booked > 0 ? (
                    <em className="agenda-day-tag">{day.booked}/{day.max}</em>
                  ) : null}
                </button>
              ))}
            </div>
            <div className="agenda-legend" data-testid="admin-legend">
              <span><i className="dot-available" /> Beschikbaar</span>
              <span><i className="dot-partial" /> Beperkt beschikbaar</span>
              <span><i className="dot-booked" /> Volgeboekt</span>
              <span><i className="dot-closed" /> Gesloten</span>
            </div>
            {selected && dayInfo ? (
              <div className="admin-editor" data-testid="admin-editor">
                <div className="admin-editor-head">
                  <h2 data-testid="admin-editor-date">{formatNL(selected)}</h2>
                  <button type="button" onClick={() => setSelected(null)} aria-label="Sluiten" data-testid="admin-editor-close">×</button>
                </div>
                <div className="admin-editor-row">
                  <label className="admin-check">
                    <input
                      type="checkbox"
                      checked={dayInfo.closed}
                      onChange={(e) => saveSettings({ closed: e.target.checked })}
                      data-testid="admin-closed-toggle"
                    />
                    Hele dag gesloten
                  </label>
                  {!dayInfo.closed ? (
                    <div className="admin-spots">
                      Plekken:
                      <button type="button" onClick={() => saveSettings({ maxBookings: Math.max(1, dayInfo.max - 1) })} data-testid="admin-spots-minus">−</button>
                      <span data-testid="admin-spots-value">{dayInfo.max}</span>
                      <button type="button" onClick={() => saveSettings({ maxBookings: Math.min(8, dayInfo.max + 1) })} data-testid="admin-spots-plus">+</button>
                    </div>
                  ) : null}
                </div>
                <ul className="admin-bookings" data-testid="admin-bookings-list">
                  {dayBookings.length === 0 ? (
                    <li className="admin-booking-empty">Nog geen boekingen op deze dag.</li>
                  ) : (
                    dayBookings.map((b) => (
                      <li key={b.id}>
                        <span>{b.label}</span>
                        <button type="button" onClick={() => delBooking(b.id)} data-testid={`admin-booking-${b.id}-delete`}>
                          Verwijderen
                        </button>
                      </li>
                    ))
                  )}
                </ul>
                <div className="admin-add">
                  <input
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder="Op wie staat de boeking?"
                    maxLength={120}
                    data-testid="admin-booking-input"
                  />
                  <button type="button" className="contact-ticket" onClick={addBooking} data-testid="admin-booking-add">
                    Boeking toevoegen<span aria-hidden="true">↗</span>
                  </button>
                </div>
                <p className="form-help">De dag is vol wanneer het aantal boekingen het aantal plekken bereikt.</p>
              </div>
            ) : null}
            </>
            )}
            <div className="admin-overview" data-testid="admin-overview">
              <h2>Komende boekingen</h2>
              {bookings.length === 0 ? (
                <p className="admin-booking-empty">Nog geen boekingen gepland.</p>
              ) : (
                <ul>
                  {bookings.map((b) => (
                    <li key={b.id}>
                      <span className="admin-overview-date">{formatNL(b.date)}</span>
                      <span className="admin-overview-label">{b.label}</span>
                      <button type="button" onClick={() => delBooking(b.id)} data-testid={`admin-booking-${b.id}-remove`}>
                        Verwijderen
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {error ? <p className="admin-error" role="alert">{error}</p> : null}
          </div>
        )}
      </motion.div>
    </div>
  );
}
